import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthHero from '../../../shared/auth/components/AuthHero';
import { useFlowProtection } from '../../../shared/hooks/useFlowProtection';

const RecruiterAccountSetup: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Flow protection - only accessible after company signup flow
    const { isValidAccess } = useFlowProtection({
        storageKey: 'fromCompanySignup',
        fallbackPath: '/auth/company/signup'
    });

    // Get email from company signup (passed via navigation state)
    const companyEmail = location.state?.email || '';
    const companyName = location.state?.companyName || '';

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: companyEmail,
        password: '',
        jobTitle: ''
    });

    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        }
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }
        if (!formData.jobTitle.trim()) {
            newErrors.jobTitle = 'Job title is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // TODO: Send recruiter account data to backend
        console.log('Recruiter Account Setup:', formData);

        // Navigate to admin welcome page
        navigate('/auth/company/admin-welcome', {
            state: {
                userName: formData.firstName,
                companyName: companyName
            }
        });
    };

    // Show nothing while checking access (redirect happens in hook if invalid)
    if (isValidAccess === null || isValidAccess === false) {
        return null;
    }

    return (
        <div className="min-h-screen bg-[#F8F9FB] flex flex-col lg:flex-row">
            {/* Left Side - Form */}
            <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
                <div className="w-full max-w-[597px]">
                    {/* Logo */}
                    <div className="mb-6 sm:mb-8">
                        <img
                            src="/branding/logo_blue.svg"
                            alt="Hush"
                            className="h-10 sm:h-12 w-auto"
                        />
                    </div>

                    {/* Header */}
                    <div className="mb-8 sm:mb-[48px]">
                        <h1
                            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#015FA6] mb-4 sm:mb-6"
                            style={{
                                fontFamily: 'Manrope',
                                lineHeight: '100%'
                            }}
                        >
                            Set up your recruiter account
                        </h1>
                        <p
                            className="text-base sm:text-lg text-[#015FA6]"
                            style={{
                                fontFamily: 'SF Pro',
                                fontWeight: 110,
                                lineHeight: '150%'
                            }}
                        >
                            Let's create your personal profile
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {/* First Name and Last Name - Two Columns */}
                        <div className="flex flex-col sm:flex-row gap-4 sm:gap-[22px] mb-6">
                            {/* First Name */}
                            <div style={{ flex: 1 }}>
                                <label
                                    htmlFor="firstName"
                                    style={{
                                        display: 'block',
                                        fontFamily: 'Manrope',
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        lineHeight: '20px',
                                        letterSpacing: '-0.154px',
                                        color: '#015FA6',
                                        marginBottom: '14px'
                                    }}
                                >
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    placeholder="Hush"
                                    style={{
                                        width: '100%',
                                        height: '55px',
                                        padding: '10px 12px',
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        color: '#015FA6',
                                        background: '#F7FAFC',
                                        border: `1px solid ${errors.firstName ? '#EF4444' : '#CBD5E0'}`,
                                        borderRadius: '8px',
                                        outline: 'none',
                                        boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                        letterSpacing: '-0.154px'
                                    }}
                                    onFocus={(e) => {
                                        if (!errors.firstName) e.target.style.borderColor = '#015FA6';
                                    }}
                                    onBlur={(e) => {
                                        if (!errors.firstName) e.target.style.borderColor = '#CBD5E0';
                                    }}
                                />
                                {errors.firstName && (
                                    <p style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '14px',
                                        color: '#EF4444',
                                        marginTop: '8px'
                                    }}>
                                        {errors.firstName}
                                    </p>
                                )}
                            </div>

                            {/* Last Name */}
                            <div style={{ flex: 1 }}>
                                <label
                                    htmlFor="lastName"
                                    style={{
                                        display: 'block',
                                        fontFamily: 'Manrope',
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        lineHeight: '20px',
                                        letterSpacing: '-0.154px',
                                        color: '#015FA6',
                                        marginBottom: '14px'
                                    }}
                                >
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    placeholder="example.com"
                                    style={{
                                        width: '100%',
                                        height: '55px',
                                        padding: '10px 12px',
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        color: '#015FA6',
                                        background: '#F7FAFC',
                                        border: `1px solid ${errors.lastName ? '#EF4444' : '#CBD5E0'}`,
                                        borderRadius: '8px',
                                        outline: 'none',
                                        boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                        letterSpacing: '-0.154px'
                                    }}
                                    onFocus={(e) => {
                                        if (!errors.lastName) e.target.style.borderColor = '#015FA6';
                                    }}
                                    onBlur={(e) => {
                                        if (!errors.lastName) e.target.style.borderColor = '#CBD5E0';
                                    }}
                                />
                                {errors.lastName && (
                                    <p style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '14px',
                                        color: '#EF4444',
                                        marginTop: '8px'
                                    }}>
                                        {errors.lastName}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Email */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                htmlFor="email"
                                style={{
                                    display: 'block',
                                    fontFamily: 'Manrope',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.154px',
                                    color: '#015FA6',
                                    marginBottom: '14px'
                                }}
                            >
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="example@gmail.com"
                                style={{
                                    width: '100%',
                                    height: '55px',
                                    padding: '10px 12px',
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    color: '#015FA6',
                                    background: '#F7FAFC',
                                    border: `1px solid ${errors.email ? '#EF4444' : '#CBD5E0'}`,
                                    borderRadius: '8px',
                                    outline: 'none',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    letterSpacing: '-0.154px'
                                }}
                                onFocus={(e) => {
                                    if (!errors.email) e.target.style.borderColor = '#015FA6';
                                }}
                                onBlur={(e) => {
                                    if (!errors.email) e.target.style.borderColor = '#CBD5E0';
                                }}
                            />
                            {errors.email && (
                                <p style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    color: '#EF4444',
                                    marginTop: '8px'
                                }}>
                                    {errors.email}
                                </p>
                            )}
                        </div>

                        {/* Password */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                htmlFor="password"
                                style={{
                                    display: 'block',
                                    fontFamily: 'Manrope',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.154px',
                                    color: '#015FA6',
                                    marginBottom: '14px'
                                }}
                            >
                                Password
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="@#*%"
                                    style={{
                                        width: '100%',
                                        height: '55px',
                                        padding: '10px 12px',
                                        paddingRight: '50px',
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        color: '#015FA6',
                                        background: '#F7FAFC',
                                        border: `1px solid ${errors.password ? '#EF4444' : '#CBD5E0'}`,
                                        borderRadius: '12px',
                                        outline: 'none',
                                        boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                        letterSpacing: '-0.154px'
                                    }}
                                    onFocus={(e) => {
                                        if (!errors.password) e.target.style.borderColor = '#015FA6';
                                    }}
                                    onBlur={(e) => {
                                        if (!errors.password) e.target.style.borderColor = '#CBD5E0';
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '16px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {showPassword ? (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 5C7 5 2.73 8.11 1 12.5 2.73 16.89 7 20 12 20s9.27-3.11 11-7.5C21.27 8.11 17 5 12 5zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="#015FA6"/>
                                        </svg>
                                    ) : (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46A11.804 11.804 0 001 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="#015FA6"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {errors.password && (
                                <p style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    color: '#EF4444',
                                    marginTop: '8px'
                                }}>
                                    {errors.password}
                                </p>
                            )}
                        </div>

                        {/* Job Title */}
                        <div style={{ marginBottom: '48px' }}>
                            <label
                                htmlFor="jobTitle"
                                style={{
                                    display: 'block',
                                    fontFamily: 'Manrope',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.154px',
                                    color: '#015FA6',
                                    marginBottom: '14px'
                                }}
                            >
                                Job Title
                            </label>
                            <input
                                type="text"
                                id="jobTitle"
                                name="jobTitle"
                                value={formData.jobTitle}
                                onChange={handleChange}
                                placeholder="Talent Acquisition Lead"
                                style={{
                                    width: '100%',
                                    height: '55px',
                                    padding: '10px 12px',
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    color: '#015FA6',
                                    background: '#F7FAFC',
                                    border: `1px solid ${errors.jobTitle ? '#EF4444' : '#CBD5E0'}`,
                                    borderRadius: '8px',
                                    outline: 'none',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    letterSpacing: '-0.154px'
                                }}
                                onFocus={(e) => {
                                    if (!errors.jobTitle) e.target.style.borderColor = '#015FA6';
                                }}
                                onBlur={(e) => {
                                    if (!errors.jobTitle) e.target.style.borderColor = '#CBD5E0';
                                }}
                            />
                            {errors.jobTitle && (
                                <p style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    color: '#EF4444',
                                    marginTop: '8px'
                                }}>
                                    {errors.jobTitle}
                                </p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className="w-full max-w-[529px] h-12 sm:h-[55px] bg-[#015FA6] hover:bg-[#014580] text-white text-base sm:text-xl font-semibold rounded-lg transition-colors"
                            style={{
                                fontFamily: 'Manrope'
                            }}
                        >
                            Complete Setup
                        </button>
                    </form>
                </div>
            </div>

            {/* Right Side - Hero */}
            <AuthHero variant="recruiter" />
        </div>
    );
};

export default RecruiterAccountSetup;
